
import React from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  Paper,
  Box,
  Link,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Chip
} from '@mui/material';
import {
  Security,
  Email,
  LocationOn,
  Phone
} from '@mui/icons-material';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      marginBottom: '0.5rem',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      marginBottom: '1rem',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      marginBottom: '0.75rem',
    },
    body1: {
      lineHeight: 1.6,
      marginBottom: '1rem',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: '24px',
          marginBottom: '24px',
        },
      },
    },
  },
});

const PrivacyPolicyComponent = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        {/* Header */}
        <AppBar position="static" color="inherit" elevation={1}>
          <Toolbar sx={{ justifyContent: 'center' }}>
            <Container maxWidth="lg">
              <Box sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Security sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h1" component="h1" color="text.primary">
                    PRIVACY POLICY
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Last updated March 31, 2025
                </Typography>
              </Box>
            </Container>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Paper elevation={2}>
            
            {/* Introduction */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="body1" color="text.primary">
                This Privacy Notice for <strong>Kali Sun Marketing</strong> ("<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"), describes how and why we might access, collect, store, use, and/or share ("<strong>process</strong>") your personal information when you use our services ("<strong>Services</strong>"), including when you:
              </Typography>
              <List sx={{ pl: 3 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <Typography variant="body1">
                    Visit our website at{' '}
                    <Link 
                      href="https://kalisunmarketing.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      color="primary"
                    >
                      https://kalisunmarketing.com
                    </Link>{' '}
                    or any website of ours that links to this Privacy Notice
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <Typography variant="body1">
                    Engage with us in other related ways, including any sales, marketing, or events
                  </Typography>
                </ListItem>
              </List>
              <Typography variant="body1" color="text.primary">
                <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at{' '}
                <Link href="mailto:support@kalisunmarketing.com" color="primary">
                  support@kalisunmarketing.com
                </Link>.
              </Typography>
            </Box>

            {/* Summary of Key Points */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                SUMMARY OF KEY POINTS
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ fontStyle: 'italic', mb: 3 }}>
                <strong>This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our{' '}
                <Link href="#toc" color="primary">table of contents</Link> below to find the section you are looking for.</strong>
              </Typography>
              
              <Box sx={{ space: 2 }}>
                {[
                  {
                    title: "What personal information do we process?",
                    content: "When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.",
                    link: "#personalinfo"
                  },
                  {
                    title: "Do we process any sensitive personal information?",
                    content: "Some of the information may be considered \"special\" or \"sensitive\" in certain jurisdictions, for example your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information."
                  },
                  {
                    title: "Do we collect any information from third parties?",
                    content: "We do not collect any information from third parties."
                  },
                  {
                    title: "How do we process your information?",
                    content: "We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.",
                    link: "#infouse"
                  },
                  {
                    title: "In what situations and with which parties do we share personal information?",
                    content: "We may share information in specific situations and with specific third parties.",
                    link: "#whoshare"
                  },
                  {
                    title: "How do we keep your information safe?",
                    content: "We have adequate organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure.",
                    link: "#infosafe"
                  },
                  {
                    title: "What are your rights?",
                    content: "Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.",
                    link: "#privacyrights"
                  },
                  {
                    title: "How do you exercise your rights?",
                    content: "The easiest way to exercise your rights is by submitting a data subject access request, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws."
                  }
                ].map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="body1" color="text.primary">
                      <strong>{item.title}</strong> {item.content}
                      {item.link && (
                        <>
                          {' '}Learn more about{' '}
                          <Link href={item.link} color="primary">
                            {item.link.includes('personal') ? 'personal information you disclose to us' :
                             item.link.includes('infouse') ? 'how we process your information' :
                             item.link.includes('whoshare') ? 'when and with whom we share your personal information' :
                             item.link.includes('infosafe') ? 'how we keep your information safe' :
                             'your privacy rights'}.
                          </Link>
                        </>
                      )}
                    </Typography>
                  </Box>
                ))}
                
                <Typography variant="body1" color="text.primary">
                  Want to learn more about what we do with any information we collect?{' '}
                  <Link href="#toc" color="primary">Review the Privacy Notice in full</Link>.
                </Typography>
              </Box>
            </Box>

            {/* Table of Contents */}
            <Box id="toc" sx={{ mb: 4 }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                TABLE OF CONTENTS
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { href: "#infocollect", text: "1. WHAT INFORMATION DO WE COLLECT?" },
                  { href: "#infouse", text: "2. HOW DO WE PROCESS YOUR INFORMATION?" },
                  { href: "#whoshare", text: "3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?" },
                  { href: "#sociallogins", text: "4. HOW DO WE HANDLE YOUR SOCIAL LOGINS?" },
                  { href: "#inforetain", text: "5. HOW LONG DO WE KEEP YOUR INFORMATION?" },
                  { href: "#infosafe", text: "6. HOW DO WE KEEP YOUR INFORMATION SAFE?" },
                  { href: "#infominors", text: "7. DO WE COLLECT INFORMATION FROM MINORS?" },
                  { href: "#privacyrights", text: "8. WHAT ARE YOUR PRIVACY RIGHTS?" },
                  { href: "#DNT", text: "9. CONTROLS FOR DO-NOT-TRACK FEATURES" },
                  { href: "#uslaws", text: "10. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?" },
                  { href: "#policyupdates", text: "11. DO WE MAKE UPDATES TO THIS NOTICE?" },
                  { href: "#contact", text: "12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" },
                  { href: "#request", text: "13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?" }
                ].map((item, index) => (
                  <Link key={index} href={item.href} color="primary" sx={{ textDecoration: 'underline' }}>
                    {item.text}
                  </Link>
                ))}
              </Box>
            </Box>

            {/* Section 1: What Information Do We Collect */}
            <Box id="infocollect" sx={{ mb: 4 }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                1. WHAT INFORMATION DO WE COLLECT?
              </Typography>
              
              <Box id="personalinfo" sx={{ mb: 3 }}>
                <Typography variant="h3" component="h3" color="text.primary">
                  Personal information you disclose to us
                </Typography>
                <Typography variant="body1" color="text.primary">
                  <strong><em>In Short:</em></strong> <em>We collect personal information that you provide to us.</em>
                </Typography>
                <Typography variant="body1" color="text.primary">
                  We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
                </Typography>
                <Typography variant="body1" color="text.primary">
                  <strong>Personal Information Provided by You.</strong> The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:
                </Typography>
                <List sx={{ pl: 3 }}>
                  {['names', 'phone numbers', 'email addresses', 'state'].map((item, index) => (
                    <ListItem key={index} sx={{ display: 'list-item', pl: 0 }}>
                      <Typography variant="body1">{item}</Typography>
                    </ListItem>
                  ))}
                </List>
                <Typography variant="body1" color="text.primary">
                  <strong>Sensitive Information.</strong> We do not process sensitive information.
                </Typography>
                <Typography variant="body1" color="text.primary">
                  <strong>Social Media Login Data.</strong> We may provide you with the option to register with us using your existing social media account details, like your Facebook, X, or other social media account. If you choose to register in this way, we will collect certain profile information about you from the social media provider, as described in the section called "{' '}
                  <Link href="#sociallogins" color="primary">HOW DO WE HANDLE YOUR SOCIAL LOGINS?</Link>" below.
                </Typography>
                <Typography variant="body1" color="text.primary">
                  All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.
                </Typography>
              </Box>
            </Box>

            {/* Section 2: How Do We Process Your Information */}
            <Box id="infouse" sx={{ mb: 4 }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                2. HOW DO WE PROCESS YOUR INFORMATION?
              </Typography>
              <Typography variant="body1" color="text.primary">
                <strong><em>In Short:</em></strong> <em>We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</em>
              </Typography>
              <Typography variant="body1" color="text.primary">
                <strong>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</strong>
              </Typography>
              <List sx={{ pl: 3 }}>
                {[
                  "To facilitate account creation and authentication and otherwise manage user accounts. We may process your information so you can create and log in to your account, as well as keep your account in working order.",
                  "To deliver and facilitate delivery of services to the user. We may process your information to provide you with the requested service.",
                  "To send administrative information to you. We may process your information to send you details about our products and services, changes to our terms and policies, and other similar information.",
                  "To deliver targeted advertising to you. We may process your information to develop and display personalized content and advertising tailored to your interests, location, and more."
                ].map((item, index) => (
                  <ListItem key={index} sx={{ display: 'list-item', pl: 0 }}>
                    <Typography variant="body1">
                      <strong>{item.split('.')[0]}.</strong> {item.split('.').slice(1).join('.')}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box id="whoshare" sx={{ mb : 4 }}>
            <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
            3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </Typography>
          <Typography variant="body2" color="text.primary">
            <strong>In Short:</strong> We may share information in specific situations described in this section and/or with the following third parties.
          </Typography>
          <Typography variant="body2" paragraph>
            We may need to share your personal information in the following situations:
          </Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
              <li><strong>Affiliates.</strong> We may share your information with our affiliates, in which case we will require those affiliates to honor this Privacy Notice. Affiliates include our parent company and any subsidiaries, joint venture partners, or other companies that we control or that are under common control with us.</li>
              <li><strong>Business Partners.</strong> We may share your information with our business partners to offer you certain products, services, or promotions.</li>
            </ul>
          </Typography>

          
        </Box>
        <Box id="sociallogins" sx={{ mb: 4 }}>  
        <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
            4. HOW DO WE HANDLE YOUR SOCIAL LOGINS?
          </Typography>
          <Typography variant="body2" color="text.primary">
            <strong>In Short:</strong> If you choose to register or log in to our Services using a social media account, we may have access to certain information about you.
          </Typography>
          <Typography variant="body2" paragraph>
            Our Services offer you the ability to register and log in using your third-party social media account details (like a Facebook or X login). Where you choose to do this, we will receive certain profile information about you from your social media provider. The profile information we receive may vary depending on the social media provider concerned, but will often include your name, email address, friends list, and profile picture, as well as other information you choose to make public on such a social media platform.
          </Typography>
          <Typography variant="body2" paragraph>
            We recommend that you review their privacy notice to understand how they collect, use, and share your personal information, and how you can set your privacy preferences on their sites and applications. Please note that we do not control, and are not responsible for, other uses of your personal information by your third-party social media provider.
          </Typography>

          
        </Box>

        <Box id="inforetain" sx={{ mb: 4 }}>
        <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
            5. HOW LONG DO WE KEEP YOUR INFORMATION?
          </Typography>
          <Typography variant="body2" color="text.primary">
            <strong>In Short:</strong> We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.
          </Typography>
          <Typography variant="body2" color="text.primary">
            We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than the period of time in which users have an account with us.
          </Typography>
          <Typography variant="body2" color="text.primary">
            When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
          </Typography>
          <Typography variant="body2" paragraph>
            We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than the period of time in which users have an account with us.
          </Typography>
          <Typography variant="body2" paragraph>
            When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
          </Typography>
          </Box>
          <Box sx={{ padding: 2 }}>
  

      
    </Box>
    <Box id="infosafe" sx={{ mb : 4 }}>
      <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
        6. HOW DO WE KEEP YOUR INFORMATION SAFE?
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>In Short:</strong> We aim to protect your personal information through a system of organizational and technical security measures.
      </Typography>
      <Typography variant="body2" paragraph>
        We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.
      </Typography>
    </Box>

    <Box id="infominors" sx={{ mb : 4 }}>
    <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
        7. DO WE COLLECT INFORMATION FROM MINORS?
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>In Short:</strong> We do not knowingly collect data from or market to children under 18 years of age.
      </Typography>
      <Typography variant="body2" paragraph>
        We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent’s use of the Services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at support@kaisummarketing.com.
      </Typography>

      
    </Box>
    <Box id="privacyrights" sx={{ mb : 4 }}>
    <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
        8. WHAT ARE YOUR PRIVACY RIGHTS?
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>In Short:</strong> You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>Withdrawing your consent:</strong> If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section <strong>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</strong> below.
      </Typography>
      <Typography variant="body2" paragraph>
        However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.
      </Typography>
      <Typography variant="body2" paragraph>
        If you have questions or comments about your privacy rights, you may email us at support@kaisummarketing.com.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>Account Information:</strong> To change your account settings, update your account, or delete your account, please contact us by email at support@kaisummarketing.com. Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
      </Typography>
      <Typography variant="body2" paragraph>
        If you would like to know whether we have collected, used, or shared your personal information, or would like to request that we transmit such information to another party or for other non-marketing purposes, please contact us by email at support@kaisummarketing.com. Upon such request, we will provide you with access to any personal information we hold about you, unless otherwise restricted by law or internal policy. Note that we will not share your information with third parties.
      </Typography>
    </Box>

    <Box sx={{ padding: 2 }}>
      <Typography variant="body2" paragraph>
        Information will not be shared with third parties.
      </Typography>

      <Typography variant="h5" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
    
        Account Information
      </Typography>
      <Typography variant="body2" paragraph>
        If you would at any time like to review or change the information in your account or terminate your account, you can:
      </Typography>
      <List>
        <ListItem>
          <Typography variant="body2">• Contact us using the contact information provided.</Typography>
        </ListItem>
      </List>
      <Typography variant="body2" paragraph>
        Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
      </Typography>
      <Typography variant="body2" paragraph>
        If you have questions or comments about your privacy rights, you may email us at support@kaisunmarketing.com.
      </Typography>
    
    </Box>
    <Box id="DNT" sx={{ mb: 4 }}>
    <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
       9. CONTROLS FOR DO-NOT-TRACK FEATURES
      </Typography>
      <Typography variant="body2" paragraph>
        Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track (“DNT”) feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Notice.
      </Typography>
      <Typography variant="body2" paragraph>
        California law requires us to let you know how we respond to web browser DNT signals. Because there currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at this time.
      </Typography>
    </Box>
            {/* Section 10: US Privacy Rights - Privacy Categories Table */}
            <Box id="uslaws" sx={{ mb: 4 }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                10. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
                <strong><em>In Short:</em></strong> <em>If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. More information is provided below.</em>
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h3" component="h3" color="text.primary">
                  Categories of Personal Information We Collect
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
                  The table below shows the categories of personal information we have collected in the past twelve (12) months. The table includes illustrative examples of each category and does not reflect the personal information we collect from you. For a comprehensive inventory of all personal information we process, please refer to the section "{' '}
                  <Link href="#infocollect" color="primary">WHAT INFORMATION DO WE COLLECT?</Link>"
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Examples</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Collected</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        {
                          category: "A. Identifiers",
                          examples: "Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name",
                          collected: "NO"
                        },
                        {
                          category: "B. Personal information as defined in the California Customer Records statute",
                          examples: "Name, contact information, education, employment, employment history, and financial information",
                          collected: "NO"
                        },
                        {
                          category: "C. Protected classification characteristics under state or federal law",
                          examples: "Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data",
                          collected: "NO"
                        },
                        {
                          category: "D. Commercial information",
                          examples: "Transaction information, purchase history, financial details, and payment information",
                          collected: "NO"
                        },
                        {
                          category: "E. Biometric information",
                          examples: "Fingerprints and voiceprints",
                          collected: "NO"
                        },
                        {
                          category: "F. Internet or other similar network activity",
                          examples: "Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements",
                          collected: "NO"
                        },
                        {
                          category: "G. Geolocation data",
                          examples: "Device location",
                          collected: "NO"
                        },
                        {
                          category: "H. Audio, electronic, sensory, or similar information",
                          examples: "Images and audio, video or call recordings created in connection with our business activities",
                          collected: "NO"
                        },
                        {
                          category: "I. Professional or employment-related information",
                          examples: "Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us",
                          collected: "NO"
                        },
                        {
                          category: "J. Education Information",
                          examples: "Student records and directory information",
                          collected: "NO"
                        },
                        {
                          category: "K. Inferences drawn from collected personal information",
                          examples: "Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics",
                          collected: "NO"
                        },
                        {
                          category: "L. Sensitive personal Information",
                          examples: "",
                          collected: "NO"
                        }
                      ].map((row, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row" sx={{ fontSize: '0.875rem' }}>
                            {row.category}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {row.examples}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                            <Chip 
                              label={row.collected} 
                              size="small" 
                              color={row.collected === 'NO' ? 'default' : 'primary'}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                
              </Box>
              <Box sx={{ padding: 2 }}>
                <Typography variant="body2" paragraph>
                  We may also collect other personal information outside of these categories through instances when you interact with us in person, online, by phone or mail in the context of:
                </Typography>
                <List>
                  <ListItem>
                    <Typography variant="body2">• Receiving help through our customer support channels;</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Participation in customer surveys or contests; and</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Facilitation in the delivery of our Services and to respond to your inquiries.</Typography>
                  </ListItem>
                </List>
                <Typography variant="body2" paragraph>
                  We will use and retain the collected personal information as needed to provide the Services or for:
                </Typography>
                <List>
                  <ListItem>
                    <Typography variant="body2">• Category H - As long as the user has an account with us</Typography>
                  </ListItem>
                </List>

                <Typography variant="h5" component="h5" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                Sources of Personal Information
                </Typography>
                <Typography variant="body2" paragraph>
                  Learn more about the sources of personal information we collect in "<a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>"
                </Typography>

                <Typography variant="h5" component="h5" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                  How We Use and Share Personal Information
                </Typography>
                <Typography variant="body2" paragraph>
                  Learn more about how we use your personal information in the section, "<a href="#infouse">HOW DO WE PROCESS YOUR INFORMATION?</a>"
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ marginTop: 4 }}>
                  Will your information be shared with anyone else?
                </Typography>
                <Typography variant="body2" paragraph>
                  We may disclose your personal information with our service providers pursuant to a written contract between us and each service provider. Learn more about how we disclose personal information to in the section "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"
                </Typography>
                <Typography variant="body2" paragraph>
                  We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be "selling" of your personal information.
                </Typography>
                <Typography variant="body2" paragraph>
                  We have not disclosed, sold, or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months. We will not sell or share personal information in the future belonging to website visitors, users, and other consumers.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ marginTop: 4 }}>
                  Your Rights
                </Typography>
                <Typography variant="body2" paragraph>
                  You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:
                </Typography>
                <List>
                  <ListItem>
                    <Typography variant="body2">• Right to know whether or not we are processing your personal data</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to access your personal data</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to correct inaccuracies in your personal data</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to obtain the deletion of your personal data</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to obtain a copy of the personal data you previously shared with us</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to non-discrimination for exercising your rights</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to opt out of the processing of your personal data if it is used for targeted advertising (or sharing as defined under California’s privacy law), the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects (profiling)</Typography>
                  </ListItem>
                </List>
                <Typography variant="body2" paragraph>
                  Depending upon the state where you live, you may also have the following rights:
                </Typography>
                <List>
                  <ListItem>
                    <Typography variant="body2">• Right to access the categories of personal data being processed (as permitted by applicable law, including the privacy law in Minnesota)</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in  California, Delaware, and Maryland)</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in  Minnesota and Oregon)</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to review, understand, question, and correct how personal data has been profiled (as permitted by applicable law, including the privacy law in Minnesota)</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including the privacy law in California)</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">• Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including the privacy law in Florida)</Typography>
                  </ListItem>
                </List>
              </Box>
              <Box sx={{ padding: 2 }}>
              <Typography variant="h5" component="h5" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
               How to Exercise Your Rights
      </Typography>
      <Typography variant="body2" paragraph>
        To exercise these rights, you can contact us by submitting a data subject access request, by emailing us at support@kaisunmarketing.com, or by referring to the contact details at the bottom of this document.
      </Typography>
      <Typography variant="body2" paragraph>
        Under certain US state data protection laws, you can designate an authorized agent to make a request on your behalf. We may deny a request from an authorized agent that does not submit proof that they have been validly authorized to act on your behalf in accordance with applicable laws.
      </Typography>

      <Typography variant="h5" component="h5" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
               Request Verification
      </Typography>
      <Typography variant="body2" paragraph>
        Upon receiving your request, we will need to verify your identity to determine you are the same person about whom we have the information in our system. We will only use personal information provided in your request to verify your identity or authority to make the request. However, if we cannot verify your identity from the information already maintained by us, we may request that you provide additional information for the purposes of verifying your identity and for security or fraud-prevention purposes.
      </Typography>
      <Typography variant="body2" paragraph>
        If you submit the request through an authorized agent, we may need to collect additional information to verify your identity before processing your request and the agent will need to provide a written and signed permission from you to submit such request on your behalf.
      </Typography>

      <Typography variant="h5" component="h5" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
               Appeals
      </Typography>
      <Typography variant="body2" paragraph>
        Under certain US state data protection laws, if we decline to take action regarding your request, you may appeal our decision by emailing us at support@kaisunmarketing.com. We will inform you in writing of any action taken or not taken in response to the appeal, including a written explanation of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state attorney general.
      </Typography>

      <Typography variant="h5" component="h5" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
               
        California "Shine The Light" Law
      </Typography>
      <Typography variant="body2" paragraph>
        California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"
      </Typography>

      
    </Box>
    <Box>
    <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
               
               11. DO WE MAKE UPDATES TO THIS NOTICE?
             </Typography>
             <Typography variant="body2" paragraph>
               <strong>In Short:</strong> Yes, we will update this notice as necessary to stay compliant with relevant laws.
             </Typography>
             <Typography variant="body2" paragraph>
               We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.
             </Typography>
    </Box>
            </Box>

            {/* Contact Section */}
            <Box id="contact" sx={{ mb: 4 }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
                If you have questions or comments about this notice, you may email us at{' '}
                <Link href="mailto:support@kalisunmarketing.com" color="primary">
                  support@kalisunmarketing.com
                </Link>{' '}
                or contact us by post at:
              </Typography>
              <Paper variant="outlined" sx={{ p: 3, backgroundColor: 'grey.50' }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    Kali Sun Marketing
                  </Typography>
                  <Typography variant="body1">1401 21st St STE R</Typography>
                  <Typography variant="body1">Sacramento, CA 95811</Typography>
                  <Typography variant="body1">United States</Typography>
                </Box>
              </Paper>
            </Box>

            {/* Final Request Section */}
            <Box id="request" sx={{ mb: 4 }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
              </Typography>
              <Typography variant="body1" color="text.primary">
                Based on the applicable laws of your country or state of residence in the US, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. To request to review, update, or delete your personal information, please{' '}
                <Link 
                  href="https://app.termly.io/notify/71e58b94-7b72-460e-992b-bca8b06f206d" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  color="primary"
                >
                  fill out and submit a data subject access request
                </Link>.
              </Typography>
            </Box>
          </Paper>
        </Container>

        {/* Footer */}
        <Paper 
          component="footer" 
          elevation={1} 
          sx={{ 
            mt: 6, 
            borderRadius: 0,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', md: 'center' }, 
                gap: 3 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Security sx={{ color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Kali Sun Marketing.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Protecting your privacy since 2025
                  </Typography>
                </Box>
              </Box>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  gap: 2 
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Link 
                    href="mailto:support@kalisunmarketing.com" 
                    color="primary" 
                    variant="body2"
                    sx={{ textDecoration: 'none' }}
                  >
                    support@kalisunmarketing.com
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Sacramento, CA
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Container>
        </Paper>
      </Box>
    </ThemeProvider>

    );
  };
  
  export default PrivacyPolicyComponent;